import type { ConfigProps } from '../../configTypes';

export interface ContributorSchemaEntry {
  title?: string;
  slug?: string;
}

export interface ContributorGroup {
  key: string;
  value: ContributorSchemaEntry[];
}

export function getContributorGroups(
  contributors: { types?: ContributorGroup[] } | unknown[] | undefined,
): ContributorGroup[] | undefined {
  if (!contributors || Array.isArray(contributors)) return undefined;
  return (contributors as { types?: ContributorGroup[] }).types;
}

export function getContributorsByType(
  groups: ContributorGroup[] | undefined,
  type: string,
): ContributorSchemaEntry[] {
  return groups?.find((g) => g.key === type)?.value ?? [];
}

const DEFAULT_CONTRIBUTORS_PATH = '/contributors/';

/**
 * Legacy domain-to-path map for tenants that haven't adopted the v2
 * `contributorsPath` config yet. Remove once every tenant has migrated.
 */
const LEGACY_DOMAIN_PATHS: Record<string, string> = {
  nursejournal: '/healthcare-review-partners/',
};

/**
 * Ensures a leading slash and trailing slash, e.g. `psychology-contributors` → `/psychology-contributors/`.
 */
export function normalizeContributorsBasePath(input: string): string {
  const t = input.trim();
  if (!t) return DEFAULT_CONTRIBUTORS_PATH;
  let p = t.startsWith('/') ? t : `/${t}`;
  if (!p.endsWith('/')) p = `${p}/`;
  return p;
}

/**
 * Resolves a legacy contributor path from the tenant's domain when the
 * v2 config field is empty. Returns `undefined` when no legacy mapping exists.
 */
function legacyPathForDomain(config: ConfigProps): string | undefined {
  const domain = (config.domain ?? config.name ?? '').toLowerCase();
  for (const [key, path] of Object.entries(LEGACY_DOMAIN_PATHS)) {
    if (domain.includes(key)) return path;
  }
  return undefined;
}

/**
 * Public base path for contributor hub + profile routes.
 *
 * Resolution order:
 *  1. `config.contributors.contributorsPath` (v2 multitenant shape)
 *  2. Legacy domain-based map (backward compat for pre-config tenants)
 *  3. `/contributors/` default
 */
export function getContributorPath(config: ConfigProps): string {
  const block = config.contributors;
  const raw =
    block &&
    typeof block === 'object' &&
    typeof block.contributorsPath === 'string'
      ? block.contributorsPath
      : '';
  if (raw) return normalizeContributorsBasePath(raw);
  return legacyPathForDomain(config) ?? DEFAULT_CONTRIBUTORS_PATH;
}

/**
 * First URL segment for contributor routes, e.g. `psychology-contributors` or `contributors`.
 */
export function getContributorPathSegment(config: ConfigProps): string {
  return getContributorPath(config).replace(/^\/+|\/+$/g, '');
}
